import React, {useEffect, useState,} from "react";
import {getAllUsers, getAvailableBranches, registerUser, adminResetPassword, updateUser, deleteUser} from "../services/Request";
import {toggleUserLockStatus} from "../services/Update";
import {useGlobalContext} from "../context/GlobalState";

//MUI Imports
import {useTheme} from '@mui/material/styles';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {styled} from '@mui/material/styles';
import Tooltip, {tooltipClasses} from '@mui/material/Tooltip';
import PasswordIcon from '@mui/icons-material/Password';
import {
    DataGridPro,
    GridActionsCellItem,
    GridEditInputCell,
    GridRowEditStopReasons,
    GridRowModes,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarFilterButton,
    GridEditSingleSelectCell,
    useGridApiRef, 
    getGridStringOperators
} from '@mui/x-data-grid-pro';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    Box,
    Select,
    Typography,
    InputLabel,
    Checkbox,
    ListItemText, 
    Divider
} from "@mui/material";
import {randomId,} from '@mui/x-data-grid-generator';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import LockPersonIcon from "@mui/icons-material/LockPerson";
import DeleteIcon from '@mui/icons-material/Delete';
import {Snackbar} from "../components/Snackbar";

export const EditToolbar = (props) => {

    const {setRows, setRowModesModel, apiRef, isAnyRowBeingEdited} = props;

    const handleClick = () => {
        const id = randomId();
        setRows((oldRows) => [...oldRows, {
            id,
            firstName: '',
            lastName: '',
            email: '',
            isLocked: false,
            role: 'Reviewer',
            branches: [],
            isNew: true
        }]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: {mode: GridRowModes.Edit, fieldToFocus: 'firstName'},
        }));
        apiRef.current.scrollToIndexes({rowIndex: 0, colIndex: 0});
    }

    return (
        <GridToolbarContainer>
            <Button
                color="primary"
                startIcon={<AddIcon/>}
                onClick={handleClick}
                disabled={isAnyRowBeingEdited}
            >
                Add New User
            </Button>
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector/>
        </GridToolbarContainer>
    );
}

export function ManageUsers() {
    const [rows, setRows] = useState([]);
    const [rowModesModel, setRowModesModel] = useState({});
    const [isAnyRowBeingEdited, setIsAnyRowBeingEdited] = useState(false);
    const [userDensity, setUserDensity] = useState(localStorage.getItem('userDensity') || 'standard');
    const [openDialog, setOpenDialog] = useState(false);
    const [openUserLockStatusDialog, setOpenUserLockStatusDialog] = useState(false);
    const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [cellErrorInfo, setCellErrorInfo] = useState([]);
    const [branchesOptions, setBranchesOptions] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true)
    const theme = useTheme();
    const [userColumnWidth, setUserColumnWidth] = useState(JSON.parse(localStorage.getItem('userColumnWidth')) || {});
    const apiRef = useGridApiRef();
    const {user} = useGlobalContext();
    
    const roleColors = {
        admin: theme.palette.accent.orange,
        reviewer: theme.palette.primary.main,
        default: theme.palette.accent.success,
    };

    useEffect(() => {
        setLoadingUsers(true);
        const fetchAllUserData = async () => {
            try {
                const [usersResponse, branchesResponse] = await Promise.all([getAllUsers(), getAvailableBranches()]);
                setRows(usersResponse ? usersResponse.map((user) => ({...user, id: parseInt(user.$id) })) : []);
                setBranchesOptions(branchesResponse);
            } catch (error) {
                console.error("Error fetching user data:", error);
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchAllUserData();
    }, []);

    useEffect(() => {
        setIsAnyRowBeingEdited(Object.values(rowModesModel).some(
            (rowMode) => rowMode.mode === GridRowModes.Edit
        ));
    }, [rowModesModel])
    
    const NoRowDataDisplay = () => (
        !loadingUsers && (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                }}
            >
                <PersonOffIcon fontSize="large"/>
                <Box sx={{mt: 1}}>No User Information Found</Box>
            </Box>
        )
    );

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
    };

    const handleDeleteClick = (id) => () => {
        const user = rows.find(row => row.id === id)
        setSelectedUser(user)
        setOpenDeleteUserDialog(true)
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) {
            console.error("No user selected.");
            return;
        }

        try {
            const response = await deleteUser(selectedUser.email);
            const {status, data} = response;
            Snackbar({message: data, variant: status === 200 ? 'success' : 'error'})

            if (status === 200) {
                setRows(rows.filter(row => row.id !== selectedUser.id));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setOpenDeleteUserDialog(false);
        }
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });

        const editedRow = rows.find((row) => row.id === id);
        if (editedRow.isNew) {
            setRows(rows.filter((row) => row.id !== id));
        }
    };

    const handleOpenDialog = (id) => {
        const user = rows.find(row => row.id === id);
        if (user) {
            setSelectedUser(user);
            setOpenDialog(true);
        } else {
            console.error("No email selected.")
        }
    }

    const handleLockUser = (id) => {
        setSelectedUser(rows.find(row => row.id === id));
        setOpenUserLockStatusDialog(true);
    };

    const handleConfirmLockUser = async () => {
        if (selectedUser) {
            try {
                const response = await toggleUserLockStatus(selectedUser?.email);
                const message = response.data;
                Snackbar({message: message, variant: response.status === 200 ? 'success' : 'error'});                
                if (response.status === 200) {
                    setRows(rows.map((row) => row.id === selectedUser.id ? {...row, isLocked: !row.isLocked} : row));
                }
            } catch (error) {
                console.error(error);
            }
        } else {
            console.error("No user selected.");
        }
        setOpenUserLockStatusDialog(false);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    }

    const handlePasswordReset = async () => {
        try {
            if (selectedUser) {
                const response = await adminResetPassword(selectedUser?.email);
                if (response.status !== 200) {
                    Snackbar({message: "Failed to send password reset link.", variant: 'error'});
                } else {
                    Snackbar({message: "Password reset link sent to user's email.", variant: 'success'});
                }
            }
        } catch (error) {
            console.error(error);
        }
        handleCloseDialog();
    }

    useEffect(() => {
        localStorage.setItem("userColumnWidth", JSON.stringify(userColumnWidth))
    }, [userColumnWidth]);
    
    const updateStoredColumnWidth = (params) => {
        try {
            const { colDef, width } = params;
            const headerName = colDef?.headerName;
            setUserColumnWidth({ ...userColumnWidth, [headerName]: width });
        } catch (error) {
            console.error(error)
        }
    };
    
    const ConfirmationDialog = () => {
        return (
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
            >
                <DialogTitle>{"Confirm Action"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to send a password reset link to {selectedUser?.email}? This action will invalidate
                        the user's current password, preventing them from logging in until they have created a new one.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handlePasswordReset} autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>
        )
    }

    const UpdateUserStatusDialog = () => {
        return (
            <Dialog open={openUserLockStatusDialog} onClose={() => setOpenUserLockStatusDialog(false)}>
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to {selectedUser?.isLocked ? 'unlock' : 'lock'} the account for {selectedUser?.email}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUserLockStatusDialog(false)}>Cancel</Button>
                    <Button onClick={handleConfirmLockUser} autoFocus>Confirm</Button>
                </DialogActions>
            </Dialog>
        )
    };

    const DeleteUserDialog = () => {
        return (
            <Dialog open={openDeleteUserDialog} onClose={() => setOpenDeleteUserDialog(false)}>
                <DialogTitle>Confirm Deletion of User</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete <b>{selectedUser?.email}</b>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteUserDialog(false)}>Cancel</Button>
                    <Button onClick={handleDeleteUser}>Confirm</Button>
                </DialogActions>
            </Dialog>
        )
    }

    const handleDensityChange = (newDensity) => {
        localStorage.setItem('userDensity', newDensity);
        setUserDensity(newDensity);
    };

    const processRowUpdate = async (row) => {
        const cellErrors = validateCell(row);
        if (cellErrors.length > 0) {
            setCellErrorInfo(cellErrors);
            return;
        }
        
        let request;
        if (row?.isNew) {
            request = await registerUser(row);
        } else {
            request = await updateUser(row);
        }

        if (request && request?.status === 200) {
            let alertText = request.data || request.response.data;
            Snackbar({message: alertText, variant: 'success'});
            const updatedRow = {...row, isNew: false};
            setRows(rows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
            return updatedRow;
        } else {
            let alertText = row.isNew ? "User registration failed: " : "User update failed: ";
            alertText += request.data || request.data.message;
            Snackbar({message: alertText, variant: 'error'});
            setCellErrorInfo([...cellErrorInfo, {id: null, message: null}]);
        }
    };

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };


    const StyledTooltip = styled(({className, ...props}) => (
        <Tooltip {...props} classes={{popper: className}}/>
    ))(({theme}) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.accent.error,
            color: theme.palette.white,
        },
    }));

    function EditInputCell(props) {
        const {id, field} = props;
        const error = cellErrorInfo.find(err => err.id === id && err.field === field);

        return (
            <StyledTooltip open={!!error} title={error ? error.message : ''}>
                {field === 'role' ?
                    <GridEditSingleSelectCell {...props}
                                              style={{border: error ? '2px solid red' : 'none', borderRadius: 4}}/>
                    :
                    <GridEditInputCell{...props} style={{border: error ? '2px solid red' : 'none', borderRadius: 4}}/>
                }
            </StyledTooltip>
        );
    }

    function renderEditCell(params) {
        if (params.field === 'branches') {
            return <BranchesEditInputCell {...params} />;
        }
        return <EditInputCell {...params} />;
    }

    function BranchesEditInputCell({id, value, api, field}) {
        const sortedBranches = [...value.map(branch => branch.branchName)].sort();

        const updateBranches = (selectedBranches) => {
            const updatedBranches = branchesOptions.filter(branch => selectedBranches.includes(branch.name))
                .map(branch => ({ branchId: branch.id, branchName: branch.name }));

            api.setEditCellValue({ id, field, value: updatedBranches });
        };

        const handleSelectChange = (event) => {
            const selectedBranches = event.target.value;

            if (selectedBranches.includes("select-all")) {
                updateBranches(branchesOptions.map(branch => branch.name));
            } else if (selectedBranches.includes("deselect-all")) {
                updateBranches([]);
            } else {
                updateBranches(selectedBranches);
            }
        };

        return (
            <Select
                id={`branches-select-${id}`}
                multiple
                variant="standard"
                value={sortedBranches || []}
                onChange={handleSelectChange}
                renderValue={(selected) => selected.join(', ')}
                sx={{
                    width: '100%',
                    fontSize: 'inherit',
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
            >
                <MenuItem value="select-all" sx={{ color: theme.palette.primary.main }}>
                    Select All
                </MenuItem>
                <MenuItem value="deselect-all" sx={{ color: theme.palette.primary.main }}>
                    Deselect All
                </MenuItem>
                <Divider />
                {branchesOptions.map((branch) => (
                    <MenuItem key={branch.id} value={branch.name}>
                        {branch.name}
                    </MenuItem>
                ))}
            </Select>
        );
    }

    function validateCell(row) {
        const errors = [];

        if (!row.firstName || row.firstName.length < 1) {
            errors.push({id: row.id, field: 'firstName', message: "First name cannot be empty"});
        }

        if (!row.lastName || row.lastName.length < 1) {
            errors.push({id: row.id, field: 'lastName', message: "Last name cannot be empty"});
        }

        const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
        if (!emailRegex.test(row.email)) {
            errors.push({id: row.id, field: 'email', message: "Please input a valid email address"});
        }

        return errors;
    }
    
    const handleProcessRowUpdateError = (error) => {
        console.log("Error updating row:", error);
    };

    /**
     * Custom filter component for the Branches column that allows multiple branch selection.
     */
    function BranchMultiSelectFilter(props) {
        const {item, applyValue} = props;
        const sortedBranches = [...branchesOptions].sort();
        
        const handleFilterChange = (event) => {
            applyValue({...item, value: event.target.value});
        };
        
        return (
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 48,
                }}
            >
                <InputLabel id="branch-select-label">Branch</InputLabel>
                <Select
                    labelId="branch-select-label"
                    id="branch-select"
                    multiple
                    variant="standard"
                    value={item.value || []}
                    label="Branch"
                    fullWidth
                    onChange={handleFilterChange}
                    renderValue={(selected) => selected.join(', ')}
                >
                    {sortedBranches?.map((branch) => (
                        <MenuItem key={branch.id} value={branch.name}>
                            <Checkbox checked={item?.value?.indexOf(branch.name) > -1}/>
                            <ListItemText primary={branch.name}/>
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        );
    }

    function StatusSelectFilter(props) {
        const {item, applyValue} = props;

        const handleFilterChange = (event) => {
            applyValue({...item, value: [event.target.value]});
        }

        return (
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 48,
                }}
            >
                <InputLabel id="status-select-label">Status</InputLabel>
                <Select
                    labelId="status-select-label"
                    id="status-select"
                    variant="standard"
                    value={item.value || ""}
                    label="Status"
                    fullWidth
                    onChange={handleFilterChange}
                >
                    <MenuItem value={true}>Locked</MenuItem>
                    <MenuItem value={false}>Unlocked</MenuItem>
                </Select>
            </Box>
        );
    }

    const columns = [
        {
            field: 'firstName',
            headerName: 'First',
            type: 'text',
            width: userColumnWidth['First'] ?? '120',
            editable: true,
            filterable: true,
            filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains' || operator.value === 'isAnyOf'),
            renderEditCell,
        },
        {
            field: 'lastName',
            headerName: 'Last',
            type: 'text',
            width: userColumnWidth['Last'] ?? '130',
            align: 'left',
            headerAlign: 'left',
            editable: true,
            filterable: true,
            filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains' || operator.value === 'isAnyOf'),
            renderEditCell,
        },
        {
            field: 'email',
            headerName: 'Email',
            type: 'email',
            width: userColumnWidth['Email'] ?? '300',
            editable: true,
            filterable: true,
            filterOperators: getGridStringOperators().filter((operator) => operator.value === 'contains' || operator.value === 'isAnyOf'),
            renderEditCell,
        },
        {
            field: 'isLocked',
            headerName: 'Status',
            type: 'singleSelect',
            valueOptions: [{label: 'Locked', value: true}, {label: 'Unlocked', value: false}],
            align: 'left',
            headerAlign: 'left',
            width: userColumnWidth['Status'] ?? '120',
            editable: true,
            renderCell: params => params.value === true ? 'Locked' : 'Unlocked',
            filterable: true,
            filterOperators: [
                {
                    label: 'is',
                    value: 'is',
                    getApplyFilterFn: (filterItem) => (userIsLocked) => !filterItem.value || userIsLocked === filterItem.value[0],
                    InputComponent: StatusSelectFilter,
                },
            ],
        },
        {
            field: 'branches',
            headerName: 'Branches',
            width: userColumnWidth['Branches'] ?? '540',
            editable: true,
            sortable: false,
            renderEditCell,
            renderCell: (params) => {
                const sortedBranches = [...params.value].map((branch) => branch.branchName).sort().join(', ');
                return (
                    <div title={sortedBranches}>
                        {sortedBranches}
                    </div>
                );
            },
            filterable: true,
            filterOperators: [
                {
                    label: 'includes',
                    value: 'includes',
                    getApplyFilterFn: (filterItem) => (userBranches) =>
                        !filterItem.value || filterItem.value.every((branch) => userBranches.some(userBranch => userBranch.branchName === branch)),
                    InputComponent: BranchMultiSelectFilter,
                },
                {
                    label: 'excludes',
                    value: 'excludes',
                    getApplyFilterFn: (filterItem) => (userBranches) =>
                        !filterItem.value || filterItem.value.length === 0 || !filterItem.value.some((branch) => userBranches.some(userBranch => userBranch.branchName === branch)),
                    InputComponent: BranchMultiSelectFilter,
                },
            ],
        },
        {
            field: 'role',
            headerName: 'Role',
            width: userColumnWidth['Role'] ?? '120',
            editable: true,
            type: 'singleSelect',
            valueOptions: ['Admin', 'Reviewer', 'Viewer'],
            renderCell: (params) => {
                const color = roleColors[params.value.toLowerCase()] || roleColors['default'];
                return <div
                    style={{
                        backgroundColor: color,
                        color: theme.palette.white,
                        textAlign: "center"
                    }}>{params.value}</div>;
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: '150',
            resizable: false,
            cellClassName: 'actions',
            getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon/>}
                            label="Save"
                            onClick={handleSaveClick(id)}
                            sx={{
                                color: "primary.main",
                            }}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon/>}
                            label="Cancel"
                            onClick={handleCancelClick(id)}
                            sx={{
                                color: "error.main"
                            }}
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon/>}
                        title="Edit"
                        label="Edit"
                        onClick={handleEditClick(id)}
                        disabled={isAnyRowBeingEdited}
                    />,
                    <GridActionsCellItem
                        icon={<PasswordIcon/>}
                        title="Reset Password"
                        label="Reset Password"
                        onClick={() => handleOpenDialog(id)}
                        disabled={isAnyRowBeingEdited}
                    />,
                    <GridActionsCellItem
                        icon={<LockPersonIcon/>}
                        title={rows[id]?.isLocked ? 'Unlock User' : 'Lock User'}
                        label={rows[id]?.isLocked ? 'Unlock User' : 'Lock User'}
                        onClick={() => handleLockUser(id)}
                        disabled={
                            isAnyRowBeingEdited ||
                            rows.some(row => row?.id === id && row.email?.toLowerCase() === user.username.toLowerCase())
                        }
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon/>}
                        title="Delete User"
                        label="Delete"
                        onClick={handleDeleteClick(id)}
                        disabled={
                            isAnyRowBeingEdited ||
                            rows.some(row => row?.id === id && row.email?.toLowerCase() === user.username.toLowerCase())
                        }
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <title>Manage Users</title>
            <Box
                boxSizing="border-box"
                sx={{
                    backgroundColor: "primary.componentbg",
                    width: '100%',
                    height: 'calc(100vh - 16px)',
                    padding: 1,
                    borderRadius: 1,
                    '& .actions': {
                        color: 'text.secondary',
                    },
                    '& .textPrimary': {
                        color: 'text.primary',
                    },
                }}
            >
                <Typography variant="h5" style={{textAlign: "left"}}>Manage Users</Typography>
                <DataGridPro
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    loading={loadingUsers}
                    initialState={{
                        sorting: {sortModel: [{field: "firstName", sort: "asc"}]},
                    }}
                    pinnedColumns={{right: ['actions']}}
                    disableColumnPinning
                    onColumnWidthChange={updateStoredColumnWidth}
                    density={userDensity}
                    onDensityChange={(newDensity) => handleDensityChange(newDensity)}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    disableColumnSelector={true}
                    onRowModesModelChange={handleRowModesModelChange}
                    onRowEditStop={handleRowEditStop}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    onRowEditStart={(params, event) => {
                        if (isAnyRowBeingEdited) {
                            event.defaultMuiPrevented = true;
                        }
                    }}
                    isCellEditable={(params) => params.field === 'email' || params.field === 'isLocked' ? params.row.isNew : true}
                    disableColumnSorting={isAnyRowBeingEdited}
                    disableColumnMenu={isAnyRowBeingEdited}
                    slots={{
                        toolbar: EditToolbar, noRowsOverlay: NoRowDataDisplay,
                    }}
                    slotProps={{
                        toolbar: {setRows, setRowModesModel, apiRef, isAnyRowBeingEdited},
                    }}
                    localeText={{
                        footerRowSelected: (count) => `${count} user${count !== 1 ? 's' : ''} selected`,
                        footerTotalRows: "Total Users:"
                    }}
                    sx={{
                        height: 'calc(100% - 32px)',
                        "& .MuiDataGrid-container--top [role=row], & .MuiDataGrid-container--bottom [role=row], & .MuiDataGrid-columnHeader": {
                            backgroundColor: "primary.background",
                        },
                        '.MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold !important',
                        },
                    }}
                />
                <ConfirmationDialog/>
                <UpdateUserStatusDialog/>
                <DeleteUserDialog/>
            </Box>
        </>
    );
}